import {
  type PostApiAdminSyncTreeRequestBody,
  useGetCompanyById,
  usePatchApiAdminCompany,
} from '@sit/client-shared';
import {
  ForceFullSyncObject,
  type ForceFullSyncObjectType,
  enumToArray,
} from '@sit/core';
import { noop } from '@tanstack/react-query';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import { CardColumn, CardRow } from 'carbon-react/lib/components/card';
import { Checkbox } from 'carbon-react/lib/components/checkbox';
import Divider from 'carbon-react/lib/components/divider';
import Heading from 'carbon-react/lib/components/heading';
import Loader from 'carbon-react/lib/components/loader';
import Message from 'carbon-react/lib/components/message';
import pull from 'lodash/pull';
import moment from 'moment';
import { type ChangeEvent, useState } from 'react';
import { Link } from 'react-router';
import { FeatureFlag } from '../../../components/FeatureFlag';
import { send } from '../../../helpers/requests';
import toastr from '../../../helpers/toastr';
import { useSyncStatus } from '../../SyncStatus/useSyncStatus';
import { isSyncEnabled } from '../helpers';

const resourceNames = {
  ALLOCATION_TEMPLATE: 'Allocation templates',
  APPROVAL: 'Approvals',
  CLASS: 'Classes',
  CONTRACT: 'Contracts',
  COSTTYPE: 'Cost types',
  CUSTOMER: 'Customers',
  DEPARTMENT: 'Departments',
  EMPLOYEEPOSITION: 'Employee positions',
  FIXEDASSET: 'Fixed assets',
  ITEM: 'Items',
  LABORCLASS: 'Labor classes',
  LABORSHIFT: 'Labor shifts',
  LABORUNION: 'Labor unions',
  LOCATION: 'Locations',
  PROJECT: 'Projects',
  PROJECTRESOURCE: 'Project resources',
  PROJECTSTATUS: 'Project statuses',
  TASK: 'Tasks',
  TASKRESOURCE: 'Task resources',
  TIMESHEET: 'Timesheets',
  TIMESHEETENTRY: 'Timesheet entries',
  TIMETYPE: 'Time types',
  VENDOR: 'Vendors',
  WAREHOUSE: 'Warehouses',
} as const;

export interface SyncStatusFormProps {
  companyTreeSettingsId: number;
  intacctCompanyId: string;
  dataSyncActive: boolean;
}

function SyncStatusForm({
  companyTreeSettingsId,
  intacctCompanyId,
  dataSyncActive,
}: SyncStatusFormProps) {
  const [state, setState] = useState<{
    refetchData: boolean;
    requestError: string | null;
    syncError: string | null;
    syncStart: Date | null;
    syncEnd: Date | null;
    selectAllForceSyncObjects: boolean;
    forceSyncObjects: ForceFullSyncObjectType[];
  }>({
    refetchData: false,
    requestError: null,
    syncError: null,
    syncStart: null,
    syncEnd: null,
    selectAllForceSyncObjects: false,
    forceSyncObjects: [],
  });

  const { data: company } = useGetCompanyById({ companyTreeSettingsId });

  const [requestError, setRequestError] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault();
    try {
      setRequestError(null);
      setLoading(true);
      const data = { intacctCompanyId };
      await send({
        method: 'POST',
        url: '/admin/release-hung-sync',
        data,
        credentials: 'include',
      });
      toastr.success('Sync job released successfully');
    } catch (error) {
      setRequestError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefetchAllDataChange = (e: ChangeEvent<HTMLInputElement>) => {
    const refetchData = e.target.checked;
    setState((prevState) => ({
      ...prevState,
      refetchData,
    }));
  };

  const handleSelectAllForceSyncObjectsChange = (
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const selectAllForceSyncObjects = e.target.checked;
    const forceSyncObjects = selectAllForceSyncObjects
      ? (enumToArray(ForceFullSyncObject) as ForceFullSyncObjectType[])
      : [];
    setState((prevState) => ({
      ...prevState,
      selectAllForceSyncObjects,
      forceSyncObjects,
    }));
  };

  const getForceFullSyncObjectChangeFunction =
    (obj: ForceFullSyncObjectType) => (e: ChangeEvent<HTMLInputElement>) => {
      const objectChecked = e.target.checked;
      const { forceSyncObjects } = state;
      if (objectChecked) {
        forceSyncObjects.push(obj);
      } else {
        pull(forceSyncObjects, obj);
      }
      setState((prevState) => ({
        ...prevState,
        forceSyncObjects,
      }));
    };

  const manualSync = async () => {
    try {
      const data: PostApiAdminSyncTreeRequestBody = {
        companyTreeSettingsId,
        refetchData: state.refetchData,
      };
      await send({
        method: 'POST',
        url: '/admin/sync-tree',
        data,
        credentials: 'include',
      });
      setState((prevState) => ({
        ...prevState,
        requestError: null,
        companyTreeSettingsId,
      }));
      toastr.success('Sync job submitted');
    } catch (error: unknown) {
      setState((prevState) => ({
        ...prevState,
        requestError: (error as Error).message,
        requestSuccess: false,
      }));
    }
  };

  const mutation = usePatchApiAdminCompany();

  const updateCompany = async () => {
    await mutation.mutateAsync(
      {
        body: {
          forceFullSync: state.forceSyncObjects,
        },
        pathParams: {
          id: `${companyTreeSettingsId}`,
        },
      },
      {
        onSuccess: () => {
          toastr.success('Company updated');
        },
        onError: (error) => {
          toastr.error(
            error instanceof Error ? error.message : (error as string),
          );
        },
      },
    );
  };

  const { data, progress } = useSyncStatus(companyTreeSettingsId, (error) => {
    setState((prevState) => ({
      ...prevState,
      requestError: error,
    }));
  });

  const atLeastOneEntityIsSyncEnabled = company?.entities.some((entity) =>
    isSyncEnabled(entity),
  );

  return (
    <>
      {data && (
        <div>
          <Checkbox
            readOnly
            label="Triggers enabled"
            checked={dataSyncActive}
            onChange={noop}
          />
          <Divider type="horizontal" variant="typical" />
          {!state.requestError && data.syncStart && !data.syncEnd && (
            <Message variant="info">
              Last sync started {moment(data.syncStart).fromNow()}
            </Message>
          )}
          {!state.requestError && data.syncEnd && !(progress < 100) && (
            <Message variant="info">
              Synced successfully {moment(data.syncEnd).fromNow()}
            </Message>
          )}

          {progress < 100 && (
            <Message variant="info">Sync in progress {progress}%</Message>
          )}
        </div>
      )}
      {companyTreeSettingsId != null && (
        <div className="intacct-sync-status">
          <FeatureFlag featureName="ENABLE_ADMIN_REFETCH_ALL_DATA">
            <Divider type="horizontal" variant="typical" />
            <Checkbox
              label="Refetch all data"
              checked={state.refetchData}
              onChange={handleRefetchAllDataChange}
            />
          </FeatureFlag>
          <Box m={1}>
            {!atLeastOneEntityIsSyncEnabled && (
              <Message variant="warning">
                To sync, click{' '}
                <code style={{ fontSize: '12px' }}>Re-enable sync</code> on at
                least one entity in the{' '}
                <Link to={'entities'}>entities page</Link>.
              </Message>
            )}
          </Box>
          <Box display="flex" flexDirection="row" justifyContent="flex-end">
            <Box mr={2}>
              <Button type="submit" onClick={handleSubmit}>
                {loading ? (
                  <Loader isActive isInsideButton />
                ) : (
                  'Release hung sync job'
                )}
              </Button>
            </Box>
            <Box>
              <Button
                buttonType="primary"
                onClick={manualSync}
                disabled={
                  !atLeastOneEntityIsSyncEnabled ||
                  (progress ? progress < 100 : false)
                }
              >
                Sync now
              </Button>
            </Box>
          </Box>
        </div>
      )}
      {(state.requestError || requestError) && (
        <Message variant="error">{state.requestError || requestError}</Message>
      )}
      <Divider type="horizontal" variant="typical" />
      <Heading
        title="Force Full Sync"
        subheader="A full sync will be forced on the following objects during the next sync"
      />
      <Checkbox
        label="Select all"
        checked={state.selectAllForceSyncObjects}
        onChange={handleSelectAllForceSyncObjectsChange}
      />
      {enumToArray(ForceFullSyncObject).map((obj) => (
        <Checkbox
          key={obj}
          label={
            obj in resourceNames
              ? resourceNames[obj as keyof typeof resourceNames]
              : obj
          }
          checked={state.forceSyncObjects.includes(
            obj as ForceFullSyncObjectType,
          )}
          onChange={getForceFullSyncObjectChangeFunction(
            obj as ForceFullSyncObjectType,
          )}
        />
      ))}

      <CardRow>
        <CardColumn align="right">
          <Button buttonType="primary" onClick={updateCompany}>
            Save
          </Button>
        </CardColumn>
      </CardRow>
    </>
  );
}

export default SyncStatusForm;
